import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  ContextService,
  LabelsService,
  Language,
  provideLocaleId,
  provideOverlayContainer,
  provideTypology,
  withApimInterceptors,
} from '@bcef/ia-shared';
import { DATA_LAYER_SELECTORS_TOKEN, provideDataLayer } from '@difa/ia-data-layer';
import { provideTypologyFileUrl } from '@difa/ia-typology-files';
import { provideJsonConvert } from '@difa/ia-utils';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore, Store } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { environment } from '@environments/environment';
import {
  loanEffects,
  loanReducer,
  selectCalculationByMonthlyPayment,
  selectProjectType,
} from '@features/loan/store';
import { DataLayerSelectors } from '@shared/data-layer/data-layer.type';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideState(loanReducer),
    provideEffects([...loanEffects]),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideStore(
      {
        router: routerReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    environment.providers,
    provideRouterStore({
      stateKey: 'router',
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      // turn this to `true` if you want to enable 'fondu enchaîné' effect when navigating.
      // withViewTransitions({ skipInitialTransition: true }),
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: Language.FR,
        useDefaultLang: true,
        loader: {
          provide: TranslateLoader,
          useExisting: LabelsService,
        },
      }),
    ),
    {
      provide: DATA_LAYER_SELECTORS_TOKEN,
      useFactory: (store: Store): DataLayerSelectors => ({
        selectProjectType: store.select(selectProjectType),
        selectCalculationByMonthlyPayment: store.select(selectCalculationByMonthlyPayment),
      }),
      deps: [Store],
    },
    provideLocaleId(),
    provideTypology(),
    provideTypologyFileUrl(() => {
      const context = inject(ContextService);
      return `/apps/ia-simulateur-ppi/labels/ia-simulateur-ppi-${context.language}-${context.typology}.json`;
    }),
    provideDataLayer({
      serviceName: 'simulateur-ppi',
      prefix: 'dataLayer',
      // turn this property to `true` if you want to log warnings in the
      // console if some of your DataLayer objects
      // are formatted properly (only in development mode)
      // it needs the `prefix` property to be set in order to work
      checkDataLayerObjectsIntegrity: false,
    }),
    provideAppInitializer(() => inject(LabelsService).getTranslation()),
    provideHttpClient(withInterceptorsFromDi(), withApimInterceptors()),
    provideJsonConvert(),
    provideOverlayContainer(),
  ],
};
